/* The Way of the Warrior — 8-Bit Line music.
   - On by default on every 8-Bit page. Browsers block sound until the visitor
     interacts, so if autoplay is blocked the music starts on the first click,
     tap or key press anywhere on the page.
   - Remembers if the visitor turned it OFF (localStorage) and keeps the loop
     position between 8-Bit pages (sessionStorage).
   - With no custom track set, plays an original chiptune generated live with
     the Web Audio API (no audio file is downloaded). */
(() => {
  const button = document.querySelector('[data-wotw-sound]');
  if (!button) return;

  const PREF_KEY = 'wotw-music';
  const POS_KEY = 'wotw-music-pos';
  const trackUrl = button.dataset.track || '';
  const volume = Math.min(Math.max(Number(button.dataset.volume || 35) / 100, 0), 1);
  const label = button.querySelector('[data-wotw-sound-label]');

  const store = {
    get(area, key) {
      try {
        return window[area].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, value);
      } catch (e) {}
    },
  };

  let ctx = null;
  let master = null;
  let audio = null;
  let timer = null;
  let wanted = store.get('localStorage', PREF_KEY) !== 'off'; // visitor wants music
  let playing = false; // sound is actually running

  // ---- Original loop: 4 bars of eighth notes in A minor (MIDI numbers, null = rest)
  const BPM = 132;
  const EIGHTH = 60 / BPM / 2;
  const LEAD = [
    69, 72, 76, 81, 79, 76, 74, 76,
    72, null, 69, 72, 74, null, 76, null,
    77, 76, 74, 72, 74, 76, 79, null,
    81, null, 79, 76, 74, 72, 71, null,
  ];
  const BASS_ROOTS = [45, 41, 43, 40]; // Am, F, G, E

  const freq = (m) => 440 * Math.pow(2, (m - 69) / 12);

  const tone = (type, f, t, dur, gain) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  };

  let noiseBuffer = null;
  const noise = (t, dur, gain, highpass) => {
    if (!noiseBuffer) {
      noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    const filter = ctx.createBiquadFilter();
    const g = ctx.createGain();
    src.buffer = noiseBuffer;
    filter.type = 'highpass';
    filter.frequency.value = highpass;
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(master);
    src.start(t);
    src.stop(t + dur + 0.02);
  };

  const kick = (t) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.12);
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
    osc.connect(g).connect(master);
    osc.start(t);
    osc.stop(t + 0.16);
  };

  let step = Number(store.get('sessionStorage', POS_KEY)) || 0;
  let nextTime = 0;

  const scheduleStep = (s, t) => {
    const bar = Math.floor(s / 8) % 4;
    const note = LEAD[s % LEAD.length];
    if (note) tone('square', freq(note), t, EIGHTH * 0.9, 0.22);
    const root = BASS_ROOTS[bar] + (s % 2 ? 12 : 0);
    tone('triangle', freq(root), t, EIGHTH * 0.95, 0.5);
    if (s % 4 === 0) kick(t);
    noise(t, s % 4 === 2 ? 0.12 : 0.03, s % 4 === 2 ? 0.35 : 0.12, s % 4 === 2 ? 1500 : 7000);
  };

  const scheduler = () => {
    while (nextTime < ctx.currentTime + 0.12) {
      scheduleStep(step, nextTime);
      nextTime += EIGHTH;
      step = (step + 1) % LEAD.length;
    }
  };

  const ensureContext = () => {
    if (ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = volume * 0.25;
    master.connect(ctx.destination);
  };

  // Try to make sound. Resolves true when audio is actually running.
  const tryPlay = async () => {
    if (playing) return true;
    if (trackUrl) {
      if (!audio) {
        audio = new Audio(trackUrl);
        audio.loop = true;
        audio.volume = volume;
        const t = Number(store.get('sessionStorage', POS_KEY + '-t'));
        if (t) audio.currentTime = t;
      }
      try {
        await audio.play();
      } catch (e) {
        return false;
      }
    } else {
      ensureContext();
      if (!ctx) return false;
      try {
        await ctx.resume();
      } catch (e) {}
      if (ctx.state !== 'running') return false;
      nextTime = ctx.currentTime + 0.05;
      clearInterval(timer);
      timer = setInterval(scheduler, 25);
    }
    playing = true;
    render();
    return true;
  };

  const halt = () => {
    if (audio) audio.pause();
    clearInterval(timer);
    timer = null;
    if (ctx && ctx.state === 'running') ctx.suspend();
    playing = false;
    render();
  };

  const render = () => {
    button.setAttribute('aria-pressed', String(wanted));
    button.classList.toggle('is-waiting', wanted && !playing);
    if (label) label.textContent = wanted ? 'ON' : 'OFF';
  };

  // First interaction anywhere unlocks audio (except the music button itself,
  // which handles its own click).
  const unlockEvents = ['pointerdown', 'keydown', 'touchend'];
  const onFirstInteraction = (event) => {
    if (button.contains(event.target)) return;
    if (!wanted) return;
    tryPlay().then((ok) => {
      if (ok) unlockEvents.forEach((e) => document.removeEventListener(e, onFirstInteraction, true));
    });
  };
  unlockEvents.forEach((e) => document.addEventListener(e, onFirstInteraction, true));

  button.addEventListener('click', () => {
    wanted = !wanted;
    store.set('localStorage', PREF_KEY, wanted ? 'on' : 'off');
    if (wanted) tryPlay();
    else halt();
    render();
  });

  // Pause when the tab is hidden, resume when visible again
  document.addEventListener('visibilitychange', () => {
    if (!playing) return;
    if (document.hidden) {
      if (audio) audio.pause();
      if (ctx) ctx.suspend();
    } else {
      if (audio) audio.play().catch(() => {});
      if (ctx) ctx.resume();
    }
  });

  // Keep the loop position when moving between 8-Bit pages
  window.addEventListener('pagehide', () => {
    store.set('sessionStorage', POS_KEY, String(step));
    if (audio) store.set('sessionStorage', POS_KEY + '-t', String(audio.currentTime));
  });

  // Little "blip" when a fighter or garment is selected (only while sound is on)
  document.addEventListener('wotw:blip', () => {
    if (!playing || !ctx) return;
    const t = ctx.currentTime;
    tone('square', freq(84), t, 0.06, 0.3);
    tone('square', freq(91), t + 0.06, 0.08, 0.3);
  });

  render();
  if (wanted) tryPlay(); // works right away when the browser allows autoplay
})();
